// =============================================================================
// SendInvoicePanel.tsx
//
// Variant C UI on each Finance Board supplement-lifecycle card.
//
// Renders 3 different states based on whether the PDF has been generated
// and whether any deliveries have happened yet:
//
//   State 1 — supplement_invoice_pdf_url IS NULL
//     → "Generate invoice PDF" button (slate)
//
//   State 2 — PDF exists, no deliveries yet
//     → Invoice attached panel with Email / Text / Both chips,
//       Preview / Edit message / Regenerate chips,
//       Message preview tabs (Email | Text)
//
//   State 3 — PDF exists + 1+ deliveries
//     → Same as State 2 + delivery history strip with sent/opened/paid status
//
// All Supabase calls go through the supabase client. Edge function call is
// supplement-invoice-send. Template overrides are per-user.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  generateAndAttachInvoicePDF,
  type InvoiceDeal,
} from "../../lib/supplementInvoicePDF";
import { fetchEdge, readEdgeJson } from "../../lib/edgeError";
import { SUPABASE_URL } from "../../lib/supabase";
import { TemplateEditModal } from "./TemplateEditModal";
import { AutoRemindToggle } from "./AutoRemindToggle";
import { heicToJpeg } from "../../lib/heicToJpeg";

// --- types ------------------------------------------------------------------

export type SendInvoicePanelProps = {
  supabase: SupabaseClient;
  deal: InvoiceDeal;
  currentUserId: string;        // auth.uid() — keys the per-user template override
  currentUserFullName?: string; // for preview merge {sender_name}; falls back to "the Bardin Roofing team"
  onUpdated?: () => void;       // refetch parent
};

type Delivery = {
  id: string;
  sent_at: string;
  sent_by: string | null;
  method: "email" | "sms" | "both";
  recipient_email: string | null;
  recipient_phone: string | null;
  email_status: string;
  sms_status: string;
  first_viewed_at: string | null;
  view_count: number;
  paid_at: string | null;
  paid_amount: number | null;
  paid_via: string | null;
  balance_due: number;
  token: string;
};

type TemplateSet = {
  emailSubject: string;
  emailBody: string;
  smsBody: string;
  isPersonalOverride: boolean;
};

// --- helpers ----------------------------------------------------------------

const money = (v: number | string | null | undefined) =>
  Number(v ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const relativeTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} hr ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)} d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const firstName = (full: string | null | undefined) =>
  full?.trim().split(/\s+/)[0] ?? "there";

const mergeTemplate = (tpl: string, vars: Record<string, string>) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);

// --- invoice "cleanliness" pre-flight ---------------------------------------
//
// The PDF renders Total Invoiced + Balance Due from `final_invoice_amount`.
// The supplement lifecycle populates supplement_invoice_amount /
// supplement_approved_amount instead, so final_invoice_amount is often blank
// when Shannon reaches the collecting stage. Generating then produces a
// "Total Invoiced $0.00" doc. checkInvoiceClean() flags that (and the
// overpaid case) so the panel can prompt for a correction before generating.

type InvoiceIssue = {
  severity: "error" | "warning";
  message: string;
  fix: string;
};

function checkInvoiceClean(deal: InvoiceDeal): { issues: InvoiceIssue[]; suggestedAmount: number } {
  const finalInv = Number(deal.final_invoice_amount ?? 0);
  const preRecv = Number(deal.pre_build_amount_received ?? 0);
  const postRecv = Number(deal.post_build_amount_received ?? 0);
  const supps = Number(deal.supplement_approved_amount ?? 0);
  const totalRecv = preRecv + postRecv;

  const issues: InvoiceIssue[] = [];

  if (deal.final_invoice_amount == null || finalInv <= 0) {
    issues.push({
      severity: "error",
      message: "No invoice amount is entered, so the PDF will show Total Invoiced $0.00.",
      fix: "Enter the final invoice amount (the total billed for this job, including approved supplements).",
    });
  } else if (finalInv < totalRecv) {
    issues.push({
      severity: "warning",
      message: `Invoice amount (${money(finalInv)}) is less than payments received (${money(totalRecv)}) — the balance will show $0 and the homeowner appears overpaid.`,
      fix: "Confirm the invoice amount covers the full job total.",
    });
  }

  // Suggested clean total: keep an existing real amount, otherwise
  // received + approved supplements (balance due then = approved supplements).
  const suggestedAmount = finalInv > 0 ? finalInv : Math.max(totalRecv + supps, 0);

  return { issues, suggestedAmount };
}

// --- component --------------------------------------------------------------

export function SendInvoicePanel({ supabase, deal, currentUserId, currentUserFullName, onUpdated }: SendInvoicePanelProps) {
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState<null | "email" | "sms" | "both">(null);
  const [err, setErr] = useState<string | null>(null);
  const [previewTab, setPreviewTab] = useState<"email" | "sms">("email");
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [templates, setTemplates] = useState<TemplateSet | null>(null);
  const [previewSignedUrl, setPreviewSignedUrl] = useState<string | null>(null);
  const [preflight, setPreflight] = useState<null | { issues: InvoiceIssue[] }>(null);
  const [preflightAmt, setPreflightAmt] = useState("");
  const [preflightSaving, setPreflightSaving] = useState(false);
  // Revised-estimate attachment status. The email send attaches the latest
  // non-superseded `finalized_insurance_paperwork` document; this surfaces
  // BEFORE sending whether one is on file, with an inline upload to fix it.
  // (Jul 15, 2026 — Shannon: "the system is not adding the revised estimate".)
  const [estDoc, setEstDoc] = useState<null | { file_name: string; created_at: string }>(null);
  const [estLoaded, setEstLoaded] = useState(false);
  const [estUploading, setEstUploading] = useState(false);

  const hasPdf = !!deal.supplement_invoice_pdf_url;
  const finalInv = Number(deal.final_invoice_amount ?? 0);
  // Jul 14, 2026: the printed PDF is carrier-safe GROSS (initial_invoice_amount,
  // falling back to final on legacy deals) — the stale-compare below must track
  // the same figure the generator snapshots.
  const grossInv = Number(deal.initial_invoice_amount ?? deal.final_invoice_amount ?? 0) || finalInv;
  const approvedSupps = Number(deal.supplement_approved_amount ?? 0);
  const balanceDue = Number(deal.post_build_balance_remaining ?? 0);

  // Live total of unsettled homeowner ACH (deal_payments: stripe + 'made').
  // Read here rather than off deals.*_amount_in_flight because that rollup also
  // counts insurance checks at IINK, which the invoice must not credit.
  const [curPending, setCurPending] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("deal_payments")
        .select("amount")
        .eq("deal_id", deal.id)
        .eq("method", "stripe")
        .eq("state", "made");
      if (cancelled) return;
      setCurPending((data ?? []).reduce((a: number, r: { amount: number | string | null }) => a + Number(r.amount ?? 0), 0));
    })();
    return () => { cancelled = true; };
  }, [supabase, deal.id, deal.supplement_invoice_generated_at]);

  // --- stale-PDF detection ---------------------------------------------------
  // The PDF is a static snapshot of final_invoice_amount + payments at the time
  // it was generated. If the invoice amount was corrected, or a payment posted,
  // afterward, the printed Total Invoiced / Balance Due are wrong and the
  // homeowner would be billed the stale figure. We compare the live deal to the
  // snapshot stamped at generation (supplement_invoice_pdf_amount/_received) and
  // block sending a confirmed-stale PDF. Legacy PDFs generated before snapshots
  // existed (snapshot null) get a softer "can't verify" advisory instead.
  const STALE_EPS = 0.005;
  const curReceived =
    Number(deal.pre_build_amount_received ?? 0) + Number(deal.post_build_amount_received ?? 0);
  const snapAmount = deal.supplement_invoice_pdf_amount;
  const snapReceived = deal.supplement_invoice_pdf_received;
  const snapPending = deal.supplement_invoice_pdf_pending;
  const amountDrift =
    hasPdf && snapAmount != null && Math.abs(Number(snapAmount) - grossInv) > STALE_EPS;
  const paymentDrift =
    hasPdf && snapReceived != null && Math.abs(Number(snapReceived) - curReceived) > STALE_EPS;
  // Aug 14, 2026 — the PDF now credits unsettled homeowner ACH, so a new
  // pending payment (or one that has since settled) also makes it stale.
  // Deliberately Stripe-only, matching the generator: an insurance check at
  // IINK is not a homeowner payment and never appears on the invoice.
  // A null snapshot means the PDF predates pending tracking (Aug 14, 2026). If
  // the homeowner has money in transit, that PDF definitely overstates the
  // balance — flag it. Null snapshot + nothing pending is genuinely fine.
  const pendingDrift =
    hasPdf && curPending > 0 &&
    (snapPending == null || Math.abs(Number(snapPending) - curPending) > STALE_EPS);
  const pdfUnverifiable = hasPdf && snapAmount == null;
  const isStale = amountDrift || paymentDrift || pendingDrift;
  // A PDF we can't verify (generated before snapshot tracking existed) is
  // treated as blocking too — we can't prove it matches the current amount,
  // and that's exactly how a stale invoice slipped out before. One
  // regenerate click produces a snapshotted, verified PDF.
  const sendBlocked = isStale || pdfUnverifiable;

  // -- load deliveries --
  const loadDeliveries = useCallback(async () => {
    const { data } = await supabase
      .from("deal_invoice_deliveries")
      .select("id, sent_at, sent_by, method, recipient_email, recipient_phone, email_status, sms_status, first_viewed_at, view_count, paid_at, paid_amount, paid_via, balance_due, token")
      .eq("deal_id", deal.id)
      .is("revoked_at", null)
      .order("sent_at", { ascending: false });
    setDeliveries((data ?? []) as Delivery[]);
  }, [supabase, deal.id]);

  // -- load templates (per-user override → system default) --
  const loadTemplates = useCallback(async () => {
    const [overrideRes, sysSubjRes, sysEmailRes, sysSmsRes] = await Promise.all([
      supabase.from("invoice_template_overrides").select("*").eq("user_id", currentUserId).maybeSingle(),
      supabase.from("finance_settings").select("value_jsonb").eq("key", "invoice_template_email_subject").single(),
      supabase.from("finance_settings").select("value_jsonb").eq("key", "invoice_template_email_body").single(),
      supabase.from("finance_settings").select("value_jsonb").eq("key", "invoice_template_sms_body").single(),
    ]);
    const ov = overrideRes.data;
    setTemplates({
      emailSubject: ov?.email_subject ?? String(sysSubjRes.data?.value_jsonb ?? ""),
      emailBody:    ov?.email_body    ?? String(sysEmailRes.data?.value_jsonb ?? ""),
      smsBody:      ov?.sms_body      ?? String(sysSmsRes.data?.value_jsonb ?? ""),
      isPersonalOverride: !!ov,
    });
  }, [supabase, currentUserId]);

  // -- load revised-estimate doc (what the email send will attach) --
  const loadEstDoc = useCallback(async () => {
    const { data } = await supabase
      .from("deal_documents")
      .select("file_name, created_at")
      .eq("deal_id", deal.id)
      .eq("file_type", "finalized_insurance_paperwork")
      .is("superseded_at", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setEstDoc((data as { file_name: string; created_at: string } | null) ?? null);
    setEstLoaded(true);
  }, [supabase, deal.id]);

  // Upload a revised estimate right here (stores as finalized_insurance_paperwork,
  // exactly what supplement-invoice-send looks up). Same storage pattern as
  // FinanceDashboard.handleUploadAndAdvance.
  const handleEstUpload = useCallback(async (picked: File) => {
    setEstUploading(true);
    setErr(null);
    try {
      const upFile = await heicToJpeg(picked);
      const safeName = upFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${deal.id}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("deal-documents")
        .upload(path, upFile, { contentType: upFile.type || "application/octet-stream" });
      if (upErr) throw new Error(upErr.message);
      const { error: docErr } = await supabase.from("deal_documents").insert({
        deal_id: deal.id,
        file_name: upFile.name,
        file_type: "finalized_insurance_paperwork",
        storage_path: path,
        file_size: upFile.size,
        mime_type: upFile.type,
        uploaded_by: currentUserId,
      });
      if (docErr) throw new Error(docErr.message);
      await loadEstDoc();
    } catch (e: any) {
      setErr(`Revised estimate upload failed: ${e?.message ?? e}`);
    } finally {
      setEstUploading(false);
    }
  }, [supabase, deal.id, currentUserId, loadEstDoc]);

  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadEstDoc(); }, [loadEstDoc]);

  // -- realtime: refresh on delivery row changes --
  useEffect(() => {
    const ch = supabase
      .channel(`div-${deal.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "deal_invoice_deliveries", filter: `deal_id=eq.${deal.id}` },
        () => loadDeliveries(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [supabase, deal.id, loadDeliveries]);

  // -- preview signed URL --
  useEffect(() => {
    if (!hasPdf) { setPreviewSignedUrl(null); return; }
    let cancel = false;
    supabase.storage
      .from("deal-documents")
      .createSignedUrl(deal.supplement_invoice_pdf_url!, 3600)
      .then(({ data }) => { if (!cancel) setPreviewSignedUrl(data?.signedUrl ?? null); });
    return () => { cancel = true; };
  }, [supabase, hasPdf, deal.supplement_invoice_pdf_url]);

  // -- merge vars for preview --
  // sender_name comes from the calling component (the user's full_name).
  // The edge function uses the same field on real send, so preview matches reality.
  const previewVars = useMemo(() => ({
    first_name: firstName(deal.homeowner_name),
    homeowner_name: deal.homeowner_name ?? "",
    final_invoice: money(finalInv),
    supplement_approved_amount: money(approvedSupps),
    balance_due: money(balanceDue),
    invoice_link: "https://app.bardinroofing.com/i/[token]",
    sender_name: currentUserFullName ?? "the Bardin Roofing team",
    address: [deal.property_street, deal.property_city, deal.property_state].filter(Boolean).join(", "),
  }), [deal.homeowner_name, deal.property_street, deal.property_city, deal.property_state, finalInv, approvedSupps, balanceDue, currentUserFullName]);

  // -- handlers --

  // The actual generation. Accepts an optional field override so the
  // pre-flight "Apply change" path can generate with the just-saved amount
  // (the derived balance column is recomputed by a DB trigger, so we pass
  // post_build_balance_remaining: null and let the PDF recompute it).
  const runGenerate = useCallback(async (override?: Partial<InvoiceDeal>) => {
    setGenerating(true);
    setErr(null);
    try {
      const dealToUse = override ? { ...deal, ...override } : deal;
      await generateAndAttachInvoicePDF(supabase, dealToUse);
      onUpdated?.();
    } catch (e: any) {
      setErr(`PDF generation failed: ${e?.message ?? e}`);
    } finally {
      setGenerating(false);
    }
  }, [supabase, deal, onUpdated]);

  // Click entrypoint: pre-flight check first. If the doc isn't clean, open the
  // correction prompt; otherwise generate straight away.
  const handleGenerateClick = useCallback(() => {
    const { issues, suggestedAmount } = checkInvoiceClean(deal);
    if (issues.length > 0) {
      setPreflightAmt(suggestedAmount > 0 ? suggestedAmount.toFixed(2) : "");
      setPreflight({ issues });
    } else {
      runGenerate();
    }
  }, [deal, runGenerate]);

  // "Apply change" — save the corrected invoice amount, then generate.
  const applyPreflight = useCallback(async () => {
    const amt = Number(preflightAmt);
    if (!Number.isFinite(amt) || amt < 0) {
      setErr("Enter a valid invoice amount (0 or more).");
      return;
    }
    setPreflightSaving(true);
    setErr(null);
    try {
      const before = deal.final_invoice_amount == null ? null : Number(deal.final_invoice_amount);
      const { error } = await supabase
        .from("deals")
        .update({ final_invoice_amount: amt })
        .eq("id", deal.id);
      if (error) throw error;

      // Best-effort audit trail for the financial edit — mirrors the
      // Set Misc Credit pattern. Non-blocking: never gates PDF generation.
      supabase
        .from("finance_audit_log")
        .insert({
          deal_id: deal.id,
          actor_id: currentUserId,
          action: "final_invoice_amount_set",
          amount_delta: amt - (before ?? 0),
          before_jsonb: { final_invoice_amount: before },
          after_jsonb: { final_invoice_amount: amt, source: "invoice_preflight" },
          reason: "Set via invoice pre-flight before generating PDF",
          is_financial: true,
        })
        .then(({ error: aerr }) => { if (aerr) console.warn("finance_audit_log insert failed:", aerr.message); });

      setPreflight(null);
      await runGenerate({ final_invoice_amount: amt, post_build_balance_remaining: null });
    } catch (e: any) {
      setErr(`Couldn't save invoice amount: ${e?.message ?? e}`);
    } finally {
      setPreflightSaving(false);
    }
  }, [preflightAmt, supabase, deal.id, deal.final_invoice_amount, currentUserId, runGenerate]);

  // "Ignore & generate" — proceed with the dirty data as-is.
  const ignorePreflight = useCallback(() => {
    setPreflight(null);
    runGenerate();
  }, [runGenerate]);

  const handleSend = useCallback(async (method: "email" | "sms" | "both") => {
    if (!hasPdf) {
      setErr("Generate the PDF first.");
      return;
    }
    if (sendBlocked) {
      setErr(isStale
        ? "This invoice PDF is out of date — regenerate it before sending so the homeowner gets the correct amount."
        : "This invoice PDF predates change-tracking, so its amount can't be verified — regenerate it before sending.");
      return;
    }
    setSending(method);
    setErr(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetchEdge(
        `${SUPABASE_URL}/functions/v1/supplement-invoice-send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session?.access_token ?? ""}`,
          },
          body: JSON.stringify({ deal_id: deal.id, method }),
        },
      );
      const result = await readEdgeJson<{
        ok?: boolean;
        detail?: string;
        error?: string;
        partial_failure?: boolean;
        estimate_attached?: boolean;
        sms_opted_out?: boolean;
        email?: { status?: string };
        sms?: { status?: string };
      }>(resp);
      if (!result?.ok) {
        throw new Error(result?.detail ?? result?.error ?? `HTTP ${resp.status}`);
      }
      if (result.partial_failure) {
        setErr(`Sent partially - email: ${result.email?.status}, sms: ${result.sms?.status}. Check delivery history.`);
      } else if (result.sms_opted_out) {
        // Oct 16, 2026: the function now honors sms_opt_out (a STOP reply).
        setErr("Sent by email only — this homeowner replied STOP, so no text was sent.");
      } else if ((method === "email" || method === "both") && result.estimate_attached === false) {
        setErr("Sent — but no revised estimate was on file, so only the invoice was attached. Use the “Upload revised estimate” button above, then resend.");
      }
      await loadDeliveries();
      onUpdated?.();
    } catch (e: any) {
      setErr(`Send failed: ${e?.message ?? e}`);
    } finally {
      setSending(null);
    }
  }, [supabase, deal.id, hasPdf, isStale, sendBlocked, loadDeliveries, onUpdated]);

  const handleRegenerate = useCallback(async () => {
    if (!confirm("Regenerate the PDF? The previous version's link still works for anyone who already received it.")) return;
    handleGenerateClick();
  }, [handleGenerateClick]);

  // Pre-flight correction modal (rendered in both State-1 and State-2/3 returns).
  const preflightModal = preflight ? (
    <InvoicePreflightModal
      issues={preflight.issues}
      amount={preflightAmt}
      onAmount={setPreflightAmt}
      saving={preflightSaving}
      homeownerName={deal.homeowner_name}
      preBuildReceived={Number(deal.pre_build_amount_received ?? 0) + Number(deal.post_build_amount_received ?? 0)}
      approvedSupps={Number(deal.supplement_approved_amount ?? 0)}
      onApply={applyPreflight}
      onIgnore={ignorePreflight}
      onCancel={() => setPreflight(null)}
    />
  ) : null;

  // ============== render ===============

  // State 1: No PDF yet
  if (!hasPdf) {
    return (
      <div className="br-send-invoice">
        <div className="br-action-row">
          <button
            className="br-btn-generate"
            onClick={handleGenerateClick}
            disabled={generating}
          >
            {generating ? "Generating…" : "📄 Generate invoice PDF"}
          </button>
          <span className="br-action-hint">
            {balanceDue <= 0
              ? "Tip: enter the Supplement Invoice amount for accurate totals — generates a paid-in-full invoice when the balance is $0."
              : "Generates the PDF. Delivery options appear after."}
          </span>
        </div>
        {err && <div className="br-err">{err}</div>}
        {preflightModal}
        <style>{styles}</style>
      </div>
    );
  }

  // State 2 + 3: PDF exists
  const previewSubject = templates ? mergeTemplate(templates.emailSubject, previewVars) : "";
  const previewEmail   = templates ? mergeTemplate(templates.emailBody, previewVars) : "";
  const previewSms     = templates ? mergeTemplate(templates.smsBody, previewVars) : "";

  return (
    <div className="br-send-invoice">
      <div className={`br-invoice-attached${sendBlocked ? " br-invoice-attached--stale" : ""}`}>
        {/* Stale-PDF warning — blocks sending until regenerated */}
        {isStale && (
          <div className="br-stale">
            <div className="br-stale-head">⚠️ This invoice PDF is out of date</div>
            <ul className="br-stale-list">
              {amountDrift && (
                <li>
                  Invoice total changed — the PDF shows <b>{money(snapAmount)}</b>, the deal is now <b>{money(finalInv)}</b>.
                </li>
              )}
              {paymentDrift && (
                <li>A payment has posted since this PDF was generated, so its Balance Due is stale.</li>
              )}
              {pendingDrift && !paymentDrift && (
                <li>
                  {snapPending == null ? (
                    <>
                      This PDF was generated before pending payments were credited, and the homeowner has{" "}
                      <b>{money(curPending)}</b> in transit — its Balance Due is overstated by that much.
                    </>
                  ) : (
                    <>
                      A pending payment changed since this PDF was generated — it credited{" "}
                      <b>{money(snapPending)}</b>, the homeowner now has <b>{money(curPending)}</b> in transit.
                    </>
                  )}
                </li>
              )}
            </ul>
            <div className="br-stale-foot">Regenerate before sending, or the homeowner gets the wrong amount.</div>
            <button className="br-chip br-chip--primary" onClick={handleRegenerate} disabled={generating}>
              {generating ? "Regenerating…" : "🔄 Regenerate now"}
            </button>
          </div>
        )}
        {/* Legacy PDF (no snapshot) — amount can't be verified, so block
            sending until it's regenerated (regenerate writes the snapshot). */}
        {pdfUnverifiable && !isStale && (
          <div className="br-stale">
            <div className="br-stale-head">⚠️ This PDF’s amount can’t be verified</div>
            <div className="br-stale-foot">
              It was generated before change-tracking existed, so we can’t confirm it matches the current invoice amount. Regenerate before sending.
            </div>
            <button className="br-chip br-chip--primary" onClick={handleRegenerate} disabled={generating}>
              {generating ? "Regenerating…" : "🔄 Regenerate now"}
            </button>
          </div>
        )}
        {/* Header strip: PDF info + Preview button */}
        <div className="br-invoice-head">
          <div className="br-doc-icon">📄</div>
          <div className="br-info">
            <div className="br-info-title">invoice-{(deal.homeowner_name ?? "homeowner").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf</div>
            <div className="br-info-sub">
              Generated {relativeTime(deal.supplement_invoice_generated_at)} · {money(balanceDue)} balance
            </div>
          </div>
          {previewSignedUrl && (
            <a className="br-preview-link" href={previewSignedUrl} target="_blank" rel="noopener noreferrer">
              Preview
            </a>
          )}
        </div>

        {/* Revised-estimate attachment status (email attaches the latest
            non-superseded Finalized Insurance Paperwork doc) */}
        {estLoaded && (
          <div className={`br-est ${estDoc ? "br-est--ok" : "br-est--missing"}`}>
            {estDoc ? (
              <>
                <span className="br-est-msg">
                  📎 Revised estimate will be attached to the email: <b>{estDoc.file_name}</b>
                  <span className="br-est-sub"> · uploaded {relativeTime(estDoc.created_at)}</span>
                </span>
                <label className="br-est-btn">
                  {estUploading ? "Uploading…" : "Replace"}
                  <input
                    type="file"
                    className="br-est-input"
                    disabled={estUploading}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleEstUpload(f); e.target.value = ""; }}
                  />
                </label>
              </>
            ) : (
              <>
                <span className="br-est-msg">
                  ⚠️ <b>No revised estimate on file</b> — the email will include the invoice only.
                  Upload the carrier's revised estimate so it goes out attached.
                </span>
                <label className="br-est-btn br-est-btn--primary">
                  {estUploading ? "Uploading…" : "📎 Upload revised estimate"}
                  <input
                    type="file"
                    className="br-est-input"
                    disabled={estUploading}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleEstUpload(f); e.target.value = ""; }}
                  />
                </label>
              </>
            )}
          </div>
        )}

        {/* Send chips */}
        <div className="br-chip-label">{sendBlocked ? "Send blocked — regenerate first" : "Send to homeowner"}</div>
        <div className="br-chips">
          <button className="br-chip br-chip--primary" onClick={() => handleSend("email")} disabled={!!sending || sendBlocked} title={sendBlocked ? "Regenerate the PDF before sending" : undefined}>
            {sending === "email" ? "Sending…" : "📧 Email"}
          </button>
          <button className="br-chip br-chip--primary" onClick={() => handleSend("sms")} disabled={!!sending || sendBlocked} title={sendBlocked ? "Regenerate the PDF before sending" : undefined}>
            {sending === "sms" ? "Sending…" : "💬 Text"}
          </button>
          <button className="br-chip br-chip--primary" onClick={() => handleSend("both")} disabled={!!sending || sendBlocked} title={sendBlocked ? "Regenerate the PDF before sending" : undefined}>
            {sending === "both" ? "Sending…" : "📧💬 Both"}
          </button>
          <button className="br-chip br-chip--ghost" onClick={() => setEditOpen(true)}>
            ✏️ Edit message
          </button>
          <button className="br-chip br-chip--ghost" onClick={handleRegenerate} disabled={generating}>
            🔄 Regenerate
          </button>
        </div>

        {/* Template preview */}
        {templates && (
          <div className="br-preview-block">
            <div className="br-preview-head">
              <span className="br-preview-label">
                Message preview · {templates.isPersonalOverride ? "your personal template" : "system default"}, editable
              </span>
              <div className="br-tabs">
                <button
                  className={`br-tab ${previewTab === "email" ? "active" : ""}`}
                  onClick={() => setPreviewTab("email")}
                >📧 Email</button>
                <button
                  className={`br-tab ${previewTab === "sms" ? "active" : ""}`}
                  onClick={() => setPreviewTab("sms")}
                >💬 Text</button>
              </div>
            </div>
            <div className="br-preview-body">
              {previewTab === "email" ? (
                <>
                  <div className="br-preview-meta">
                    <b>To:</b> {deal.homeowner_email ?? "—"}  ·  <b>From:</b> {currentUserFullName ?? "Bardin Roofing"} (invoices@bardinroofing.com)
                  </div>
                  <div className="br-preview-subject"><b>Subject:</b> {previewSubject}</div>
                  <pre className="br-preview-text">{previewEmail}</pre>
                </>
              ) : (
                <>
                  <div className="br-preview-meta">
                    <b>To:</b> {deal.homeowner_phone ?? "—"}  ·  <b>From:</b> (470) 823-8816
                  </div>
                  <pre className="br-preview-text br-preview-sms">{previewSms}</pre>
                </>
              )}
            </div>
          </div>
        )}

        {/* Delivery history */}
        {deliveries.length > 0 && (
          <div className="br-history">
            <div className="br-history-label">Delivery history</div>
            {deliveries.map((d) => (
              <DeliveryRow key={d.id} d={d} />
            ))}
          </div>
        )}

        {/* Auto-remind (Oct 20, 2026): opt-in balance reminders 7 / 14 / 21 days after the final invoice. */}
        <AutoRemindToggle supabase={supabase} dealId={deal.id} />
      </div>

      {err && <div className="br-err">{err}</div>}

      {preflightModal}

      {editOpen && templates && (
        <TemplateEditModal
          supabase={supabase}
          currentUserId={currentUserId}
          initial={templates}
          previewVars={previewVars}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); loadTemplates(); }}
        />
      )}

      <style>{styles}</style>
    </div>
  );
}

// --- delivery row ---------------------------------------------------------

function DeliveryRow({ d }: { d: Delivery }) {
  const sentLabel = d.method === "both" ? "Email + SMS" : d.method === "email" ? "Email" : "SMS";
  const paid = !!d.paid_at;
  const opened = !!d.first_viewed_at;
  return (
    <div className="br-hist-item">
      <span className={`br-hist-dot ${paid ? "paid" : opened ? "opened" : "sent"}`}>
        {paid ? "$" : opened ? "👁" : "✓"}
      </span>
      <div className="br-hist-body">
        <div className="br-hist-line">
          <b>{sentLabel}</b> · {relativeTime(d.sent_at)}
          {opened && !paid && <span className="br-hist-tag">opened {relativeTime(d.first_viewed_at)}</span>}
          {paid && <span className="br-hist-tag br-hist-tag--paid">PAID {money(d.paid_amount)} via {d.paid_via}</span>}
        </div>
        <div className="br-hist-sub">
          {d.recipient_email && <span>{d.recipient_email}</span>}
          {d.recipient_email && d.recipient_phone && <span> · </span>}
          {d.recipient_phone && <span>{d.recipient_phone}</span>}
          {d.view_count > 1 && <span> · {d.view_count} views</span>}
        </div>
      </div>
    </div>
  );
}

// --- pre-flight correction modal -----------------------------------------

function InvoicePreflightModal({
  issues,
  amount,
  onAmount,
  saving,
  homeownerName,
  preBuildReceived,
  approvedSupps,
  onApply,
  onIgnore,
  onCancel,
}: {
  issues: InvoiceIssue[];
  amount: string;
  onAmount: (v: string) => void;
  saving: boolean;
  homeownerName: string | null;
  preBuildReceived: number;
  approvedSupps: number;
  onApply: () => void;
  onIgnore: () => void;
  onCancel: () => void;
}) {
  const hasError = issues.some((i) => i.severity === "error");
  const amtNum = Number(amount);
  const balancePreview = Number.isFinite(amtNum) ? Math.max(0, amtNum - preBuildReceived) : 0;
  return (
    <div className="br-pf-overlay" role="dialog" aria-modal="true" aria-labelledby="br-pf-title">
      <div className="br-pf-modal">
        <div className="br-pf-head">
          <span className="br-pf-icon">{hasError ? "⚠️" : "❗"}</span>
          <div>
            <div id="br-pf-title" className="br-pf-title">
              This invoice isn’t clean yet
            </div>
            <div className="br-pf-sub">
              {homeownerName ?? "This homeowner"}’s PDF will look off if you generate it as-is.
            </div>
          </div>
        </div>

        <ul className="br-pf-issues">
          {issues.map((iss, idx) => (
            <li key={idx} className={`br-pf-issue br-pf-issue--${iss.severity}`}>
              <div className="br-pf-issue-msg">{iss.message}</div>
              <div className="br-pf-issue-fix">→ {iss.fix}</div>
            </li>
          ))}
        </ul>

        <div className="br-pf-field">
          <label className="br-pf-label" htmlFor="br-pf-amount">Final invoice amount</label>
          <div className="br-pf-input-wrap">
            <span className="br-pf-dollar">$</span>
            <input
              id="br-pf-amount"
              className="br-pf-input"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => onAmount(e.target.value)}
              placeholder="0.00"
              autoFocus
            />
          </div>
          <div className="br-pf-context">
            Payments received: <b>{money(preBuildReceived)}</b>
            {approvedSupps > 0 && <> · Approved supplements: <b>{money(approvedSupps)}</b></>}
            {" · "}Balance due will show: <b>{money(balancePreview)}</b>
          </div>
        </div>

        <div className="br-pf-actions">
          <button className="br-pf-btn br-pf-btn--apply" onClick={onApply} disabled={saving}>
            {saving ? "Saving…" : "Apply change & generate"}
          </button>
          <button className="br-pf-btn br-pf-btn--ignore" onClick={onIgnore} disabled={saving}>
            Ignore & generate anyway
          </button>
          <button className="br-pf-btn br-pf-btn--cancel" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// --- styles (scoped via .br-send-invoice) --------------------------------

const styles = `
.br-send-invoice { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #2a2e30; }
.br-action-row {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  background: #f3f5f6;
  border-top: 1px solid #e3e6e8;
  margin: 0 -16px -16px;
}
.br-btn-generate {
  display: inline-flex; align-items: center; gap: 8px;
  background: #2a2e30; color: white; border: none; border-radius: 10px;
  padding: 12px 18px; font-size: 14px; font-weight: 700; cursor: pointer;
}
.br-btn-generate:disabled { opacity: 0.55; cursor: not-allowed; }
.br-action-hint { font-size: 12px; color: #5b6266; }
.br-invoice-attached {
  background: white; border: 2px dashed #f46530; border-radius: 12px;
  padding: 16px; margin-top: 4px;
}
.br-invoice-attached--stale { border-color: #dc2626; border-style: solid; }
.br-stale {
  background: #fff1ea; border: 1px solid #ff9e7a; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 14px;
}
.br-stale--soft { background: #fffaeb; border-color: #fcd34d; }
.br-stale-head { font-size: 13px; font-weight: 800; color: #2a2e30; }
.br-stale-list { margin: 8px 0 6px; padding-left: 18px; font-size: 12.5px; color: #2a2e30; line-height: 1.5; }
.br-stale-list b { font-weight: 800; }
.br-stale-foot { font-size: 12px; color: #5b6266; margin-bottom: 10px; }
.br-stale--soft .br-stale-foot { margin-bottom: 0; }
.br-invoice-head {
  display: flex; align-items: center; gap: 12px; margin-bottom: 14px;
}
.br-doc-icon {
  width: 44px; height: 56px; background: white;
  border: 1px solid #f46530; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.06);
}
.br-info { flex: 1; }
.br-info-title { font-weight: 700; font-size: 14px; }
.br-info-sub { font-size: 12px; color: #5b6266; margin-top: 2px; }
.br-preview-link {
  background: #f3f5f6; border: 1px solid #e3e6e8; color: #2a2e30;
  padding: 7px 12px; border-radius: 8px; font-size: 12px; font-weight: 600;
  text-decoration: none; cursor: pointer;
}
.br-est {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  border-radius: 10px; padding: 10px 12px; margin-bottom: 12px;
  font-size: 12.5px; line-height: 1.45; color: #2a2e30;
}
.br-est--ok { background: #ecfdf5; border: 1px solid #a7f3d0; }
.br-est--missing { background: #fffaeb; border: 1px solid #fcd34d; }
.br-est-msg { flex: 1; min-width: 220px; }
.br-est-msg b { font-weight: 700; }
.br-est-sub { color: #5b6266; font-size: 11.5px; }
.br-est-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: white; color: #2a2e30; border: 1px solid #dfe6ea;
  padding: 7px 12px; border-radius: 8px; font-size: 12px; font-weight: 700;
  cursor: pointer; white-space: nowrap;
}
.br-est-btn:hover { border-color: #f46530; color: #f46530; }
.br-est-btn--primary { background: #f46530; color: white; border-color: #f46530; }
.br-est-btn--primary:hover { background: #e25624; color: white; }
.br-est-input { display: none; }
.br-chip-label {
  font-size: 11px; text-transform: uppercase; color: #5b6266;
  font-weight: 700; letter-spacing: 0.4px; margin-bottom: 8px;
}
.br-chips { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; }
.br-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 14px; border-radius: 999px; font-size: 13px;
  font-weight: 700; cursor: pointer; border: none;
}
.br-chip:disabled { opacity: 0.5; cursor: not-allowed; }
.br-chip--primary { background: #f46530; color: white; }
.br-chip--primary:hover { background: #e25624; }
.br-chip--ghost { background: white; color: #2a2e30; border: 1px solid #e3e6e8; }
.br-chip--ghost:hover { border-color: #f46530; color: #f46530; }
.br-preview-block {
  background: white; border: 1px solid #e3e6e8; border-radius: 10px; overflow: hidden;
}
.br-preview-head {
  display: flex; align-items: center;
  background: #f3f5f6; border-bottom: 1px solid #e3e6e8;
  padding: 8px 12px;
}
.br-preview-label {
  font-size: 11px; text-transform: uppercase; font-weight: 700;
  color: #5b6266; letter-spacing: 0.4px;
}
.br-tabs { display: flex; gap: 4px; margin-left: auto; }
.br-tab {
  font-size: 11px; padding: 4px 10px;
  background: white; color: #5b6266;
  border: 1px solid #e3e6e8; border-radius: 6px;
  font-weight: 600; cursor: pointer;
}
.br-tab.active { background: #f46530; color: white; border-color: #f46530; font-weight: 700; }
.br-preview-body { padding: 14px 16px; }
.br-preview-meta { font-size: 11px; color: #5b6266; margin-bottom: 6px; }
.br-preview-subject { font-size: 12px; color: #5b6266; margin-bottom: 10px; }
.br-preview-text {
  font-size: 13px; line-height: 1.55; color: #2a2e30;
  white-space: pre-wrap; word-wrap: break-word;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  margin: 0;
}
.br-preview-sms {
  background: #f3f5f6; border-radius: 12px;
  padding: 10px 14px; max-width: 380px;
}
.br-history {
  margin-top: 14px; padding-top: 12px;
  border-top: 1px dashed #e3e6e8;
}
.br-history-label {
  margin: 0 0 8px; font-size: 11px; text-transform: uppercase;
  color: #5b6266; font-weight: 700; letter-spacing: 0.4px;
}
.br-hist-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 4px 0; font-size: 12px;
}
.br-hist-dot {
  width: 22px; height: 22px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; flex-shrink: 0;
}
.br-hist-dot.sent { background: #ecfdf5; color: #10b981; }
.br-hist-dot.opened { background: #eff6ff; color: #3b82f6; }
.br-hist-dot.paid { background: #f46530; color: white; }
.br-hist-body { flex: 1; }
.br-hist-line { font-size: 12px; color: #2a2e30; }
.br-hist-line b { font-weight: 700; }
.br-hist-tag {
  display: inline-block; margin-left: 8px;
  font-size: 10px; background: #eff6ff; color: #3b82f6;
  padding: 2px 8px; border-radius: 4px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
}
.br-hist-tag--paid { background: #ecfdf5; color: #10b981; }
.br-hist-sub { font-size: 11px; color: #5b6266; margin-top: 1px; }
.br-err {
  margin-top: 12px; padding: 10px 12px;
  background: #fff1ea; border: 1px solid #ff9e7a;
  border-radius: 8px; font-size: 13px; color: #2a2e30;
}

/* --- pre-flight correction modal --- */
.br-pf-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(20, 22, 24, 0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.br-pf-modal {
  background: white; border-radius: 16px;
  width: 100%; max-width: 460px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.35);
  padding: 22px; max-height: 90vh; overflow-y: auto;
}
.br-pf-head { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 16px; }
.br-pf-icon { font-size: 26px; line-height: 1; flex-shrink: 0; }
.br-pf-title { font-size: 17px; font-weight: 800; color: #2a2e30; }
.br-pf-sub { font-size: 13px; color: #5b6266; margin-top: 3px; }
.br-pf-issues { list-style: none; margin: 0 0 18px; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.br-pf-issue { border-radius: 10px; padding: 11px 13px; }
.br-pf-issue--error { background: #fff1ea; border: 1px solid #ff9e7a; }
.br-pf-issue--warning { background: #fffaeb; border: 1px solid #fcd34d; }
.br-pf-issue-msg { font-size: 13px; color: #2a2e30; font-weight: 600; }
.br-pf-issue-fix { font-size: 12px; color: #5b6266; margin-top: 4px; }
.br-pf-field { margin-bottom: 18px; }
.br-pf-label {
  display: block; font-size: 11px; text-transform: uppercase;
  font-weight: 700; color: #5b6266; letter-spacing: 0.4px; margin-bottom: 6px;
}
.br-pf-input-wrap { display: flex; align-items: center; border: 1.5px solid #dfe6ea; border-radius: 10px; overflow: hidden; }
.br-pf-input-wrap:focus-within { border-color: #f46530; }
.br-pf-dollar { padding: 0 12px; font-size: 16px; font-weight: 700; color: #5b6266; background: #f3f5f6; align-self: stretch; display: flex; align-items: center; }
.br-pf-input {
  flex: 1; border: none; outline: none; padding: 11px 12px;
  font-size: 16px; font-weight: 600; color: #2a2e30; width: 100%;
}
.br-pf-context { font-size: 12px; color: #5b6266; margin-top: 8px; line-height: 1.5; }
.br-pf-actions { display: flex; flex-direction: column; gap: 8px; }
.br-pf-btn { padding: 12px 16px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; border: none; }
.br-pf-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.br-pf-btn--apply { background: #f46530; color: white; }
.br-pf-btn--apply:hover { background: #e25624; }
.br-pf-btn--ignore { background: white; color: #2a2e30; border: 1px solid #dfe6ea; }
.br-pf-btn--ignore:hover { border-color: #b9c2c8; }
.br-pf-btn--cancel { background: transparent; color: #5b6266; }
.br-pf-btn--cancel:hover { color: #2a2e30; }
`;
